import { CapChart } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/CapChart';
import { useCapChartDataController } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/useCapChartDataController.tsx';
import { useCapChartSecuritiesController } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/useCapChartSecuritiesController';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { Box } from '@dealstack/frontend/shared/components/Box/Box';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils';
import {
	BreadcrumbsProvider,
	useBreadcrumbs,
} from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import {
	useTranslationCapChart,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { Flex } from '@mantine/core';
import { Context, createContext, useContext, useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { CapChartPageHeader } from './CapChartPageHeader/CapChartPageHeader';

type CapChartPageContextType = {
	capChartSecurities: ReturnType<typeof useCapChartSecuritiesController>;
	capChartData: ReturnType<typeof useCapChartDataController>;
	isChartLoading: boolean;
};

export const CapChartPageContext: Context<CapChartPageContextType> = createContext(
	{} as CapChartPageContextType
);

export const CapChartPage = () => {
	const tCommonUi = useTranslationCommonUi();
	const tCapChart = useTranslationCapChart();

	const { deals, deal } = useContext(DealContext) || {};
	const securitiesData = useCapChartSecuritiesController();
	const capData = useCapChartDataController({
		appliedSecurityIds: securitiesData.appliedSecurityIds,
		isoFilterDate: securitiesData.isoFilterDate,
	});
	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	const { dealId } = securitiesData;

	const breadcrumbs = useMemo(() => {
		const baseBreadcrumbs = makeDealPageBreadcrumbs({
			tCommonUi,
			dealId,
			dealName: deal?.name,
			deals,
			onlyDealDropdown: inPageFeatureFlag,
		});
		if (inPageFeatureFlag) {
			return baseBreadcrumbs;
		} else {
			return [
				...baseBreadcrumbs,
				{
					name: tCapChart('chart_title'),
					url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.CAP_CHART}`, { dealId }),
				},
			];
		}
	}, [inPageFeatureFlag, deal, dealId, deals, tCapChart, tCommonUi]);

	useBreadcrumbs(breadcrumbs, { skipFirst: true });

	const contextProviderValue = useMemo(
		() => ({
			capChartSecurities: securitiesData,
			capChartData: capData,
			isChartLoading:
				capData.isLoadingChartData ||
				(securitiesData.isLoadingSecurities && !securitiesData.chartSecurities),
		}),
		[securitiesData, capData]
	);

	return (
		<Flex direction="column" p="1rem 2rem" gap="2rem" w="100%" h="100%">
			<BreadcrumbsProvider>
				<CapChartPageContext.Provider value={contextProviderValue}>
					<Flex direction="column" gap="1rem" h="100%">
						<CapChartPageHeader />
						<Box
							style={{
								boxShadow: 'none',
								padding: 0,
								height: '100%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								overflow: 'hidden',
							}}
						>
							<CapChart />
						</Box>
					</Flex>
				</CapChartPageContext.Provider>
			</BreadcrumbsProvider>
		</Flex>
	);
};
